/**
 * src/three/ThreeGame.js
 * FPS ระบายอารมณ์ — shake / hitstop / rage / combo / คำเอง / เป้า
 */
import * as THREE from 'three';
import { T3, COLORS, RAGE_PHRASES, RELEASE_MESSAGES, BULLET_CHOICES, BULLET_BASE_COUNT, BULLET_MAX_COUNT } from './config.js';
import BrickWall3D from './BrickWall3D.js';
import GunViewmodel from './GunViewmodel.js';
import SoundFx from './SoundFx.js';
import BgMusic from './BgMusic.js';
import RageBulletDom from './RageBulletDom.js';
import RageInputDom from './RageInputDom.js';
import Breakthrough from './Breakthrough.js';
import { createPixelNumberSprite } from './pixelFont.js';
import { createDissolveSprite } from './targetLabel.js';
import { getUrlParams, appendUrlParams } from '../utils/urlParams.js';

function randInt(a, b) {
  return a + Math.floor(Math.random() * (b - a + 1));
}

function createClock() {
  let last = performance.now();
  let elapsedTime = 0;
  return {
    get elapsedTime() {
      return elapsedTime;
    },
    start() {
      last = performance.now();
      elapsedTime = 0;
    },
    getDelta() {
      const now = performance.now();
      const dt = (now - last) / 1000;
      last = now;
      elapsedTime += dt;
      return dt;
    },
  };
}

function tick() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

export default class ThreeGame {
  constructor(container, opts = {}) {
    this.container = container;
    this.onHud = opts.onHud || (() => {});
    this.onReady = opts.onReady || (() => {});
    this.onProgress = opts.onProgress || (() => {});
    this.onPlayEvent = opts.onPlayEvent || (() => {});
    this.running = false;
    this._aborted = false;
    this.paused = false;
    this.pointerLocked = false;

    this.score = 0;
    this.fireCooldown = 0;
    this.isFiring = false;
    this.yaw = 0;
    this.pitch = 0;

    this.ammoUnlimited = T3.AMMO_UNLIMITED !== false;
    this.ammoMag = T3.AMMO_MAG ?? 30;
    this.ammoReserve = T3.AMMO_RESERVE ?? 120;
    this.ammo = this.ammoMag;

    this.isMobile = this.detectMobile();
    this.mobileStarted = false;
    this.lookTouchId = null;
    this.lookLastX = 0;
    this.lookLastY = 0;
    this.fireLookActive = false;
    this.fireLookLastX = 0;
    this.fireLookLastY = 0;

    this.rage = 0;
    this.berserk = false;
    this.berserkTimer = 0;
    this.combo = 0;
    this.comboTimer = 0;
    this.maxCombo = 0;
    this.waveMaxCombo = 0;
    this.berserkUsedThisWave = false;
    this.waveStartedAt = 0;
    this.sessionStartedAt = 0;
    this.playSessionStarted = false;
    this.shake = 0;
    this.hitStop = 0;
    this.releaseMsg = '';
    this.releaseMsgTimer = 0;
    this.customPhrases = [];
    this.bulletFullAlert = false;
    this.bulletChoices = (BULLET_CHOICES || []).slice(0, BULLET_BASE_COUNT);
    while (this.bulletChoices.length < BULLET_BASE_COUNT) {
      this.bulletChoices.push(RAGE_PHRASES[this.bulletChoices.length] || 'อัด!');
    }
    /** index ที่เลือกเสริมเข้าพูลกระสุน — ว่าง = ใช้แบบเดิม */
    this.selectedBullets = [];
    this.lastRemaining = -1;
    this.stageClear = false;
    this.clearWave = 1;

    this.camBase = new THREE.Vector3(0, 0.18, 0.85);
    this.camHome = this.camBase.clone();
    this.surveyReady = false;
    this.surveyTimer = 0;
    this.revealT = 0;
    this.revealYaw0 = 0;
    this.revealPitch0 = 0;
    this.camRevealFrom = this.camHome.clone();
    this.camRevealTo = new THREE.Vector3(0, 0.1, -0.55);
    this.shakeV = new THREE.Vector3();

    this.clock = createClock();
    this.raycaster = new THREE.Raycaster();
    this.ndc = new THREE.Vector2(0, T3.AIM_NDC_Y ?? -0.08);
    this.tmpV = new THREE.Vector3();
    this.tmpV2 = new THREE.Vector3();
    this.fx = [];
    this.sfx = new SoundFx();
    this.bgm = new BgMusic();
    this.rageBullets = null;
    this.rageInput = null;
    this.aimedBrick = null;
    this.urlParams = getUrlParams();
    this.brandLink = '';

    this._lastResizeW = 0;
    this._lastResizeH = 0;
    this._resizeRaf = 0;
    this._onResize = () => {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => {
        this._resizeRaf = 0;
        this.handleResize();
      });
    };
    this._onMove = (e) => this.onPointerMove(e);
    this._onDown = () => this.onPointerDown();
    this._onUp = () => this.onPointerUp();
    this._onLockChange = () => this.onLockChange();
    this._onKey = (e) => this.onKey(e);
    this._onTouchStart = (e) => this.onTouchStart(e);
    this._onTouchMove = (e) => this.onTouchMove(e);
    this._onTouchEnd = (e) => this.onTouchEnd(e);
  }

  detectMobile() {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return true;
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
    const noHover = window.matchMedia?.('(hover: none)')?.matches;
    return !!(coarse && noHover);
  }

  getPlaySnapshot(extra = {}) {
    const remaining = this.wall?.getRemaining?.() ?? 0;
    const total = this.wall?.getTotal?.() ?? 0;
    return {
      userId: this.urlParams?.userId || '',
      brand: this.urlParams?.brand || '',
      source: this.urlParams?.source || '',
      device: this.isMobile ? 'mobile' : 'desktop',
      score: this.score,
      wave: this.wall?.wave ?? 1,
      clearWave: this.clearWave,
      remaining,
      bricksDestroyed: Math.max(0, total - remaining),
      bricksTotal: total,
      maxCombo: this.maxCombo,
      waveMaxCombo: this.waveMaxCombo,
      berserkUsed: !!this.berserkUsedThisWave,
      selectedBullets: (this.selectedBullets || [])
        .map((i) => this.bulletChoices[i])
        .filter(Boolean),
      customPhrases: (this.customPhrases || []).slice(),
      durationMs: this.waveStartedAt ? Date.now() - this.waveStartedAt : null,
      sessionDurationMs: this.sessionStartedAt ? Date.now() - this.sessionStartedAt : null,
      ...extra,
    };
  }

  emitPlay(type, extra = {}) {
    try {
      this.onPlayEvent?.({ type, ...this.getPlaySnapshot(extra) });
    } catch {
      // keep gameplay running even if analytics fails
    }
  }

  markPlayStart() {
    if (!this.waveStartedAt) this.waveStartedAt = Date.now();
    if (this.playSessionStarted) return;
    this.playSessionStarted = true;
    this.sessionStartedAt = Date.now();
    this.maxCombo = Math.max(this.maxCombo, this.combo);
    this.emitPlay('start');
  }

  endPlaySession(exitAction) {
    if (!this.playSessionStarted) return;
    this.emitPlay('end', { exitAction });
    this.playSessionStarted = false;
    this.waveStartedAt = 0;
    this.sessionStartedAt = 0;
  }

  /** เล่นได้แล้วหรือยัง (desktop = pointer lock / mobile = แตะเริ่ม) */
  isPlaying() {
    if (this.stageClear) return false;
    if (this.rageInput?.open) return false;
    return this.isMobile ? this.mobileStarted : this.pointerLocked;
  }

  setBrandLink(link) {
    this.brandLink = String(link || '').trim();
  }

  getReturnUrl() {
    const params = this.urlParams || getUrlParams();
    let url = this.brandLink || params.redirect || '';
    if (!url) {
      const configured = (T3.RETURN_URL || '').trim();
      if (configured && !/^https?:\/\/(www\.)?example\.com\/?$/i.test(configured)) {
        url = configured;
      }
    }
    if (!url) return null;
    return appendUrlParams(url, params);
  }

  hasReturnDomain() {
    return !!this.getReturnUrl();
  }

  async create() {
    const report = (pct, label) => {
      if (this._aborted) return;
      this.onProgress?.({ pct, label });
    };
    const wait = async () => {
      if (this._aborted) return false;
      await tick();
      return !this._aborted;
    };

    report(6, 'กำลังโหลดตัวอักษร');
    this.bgm?.preload?.();
    try {
      await document.fonts?.ready;
    } catch (_) { /* ignore */ }
    if (!(await wait())) return;

    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.isMobile ? 1.5 : 2));
    this._lastResizeW = Math.max(1, Math.round(w));
    this._lastResizeH = Math.max(1, Math.round(h));
    this.renderer.setSize(this._lastResizeW, this._lastResizeH, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.container.appendChild(this.renderer.domElement);
    Object.assign(this.renderer.domElement.style, {
      display: 'block', width: '100%', height: '100%', outline: 'none',
    });
    this.renderer.domElement.tabIndex = 0;
    if (!(await wait())) return;

    report(22, 'สร้างห้อง');
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(COLORS.BG);
    this.scene.fog = new THREE.FogExp2(COLORS.FOG, 0.055);

    this.camera = new THREE.PerspectiveCamera(76, w / h, 0.05, 80);
    this.camera.position.copy(this.camBase);
    this.scene.add(this.camera);

    this.buildEnvironment();
    if (!(await wait())) return;

    report(32, 'แกะลายหิน');
    this.wall = new BrickWall3D(this.scene);
    let matStep = 0;
    await this.wall.initMaterials(() => {
      matStep += 1;
      report(32 + matStep * 6, 'แกะลายหิน');
    });
    if (this._aborted) return;

    report(64, 'ก่อกำแพง');
    if (!(await wait())) return;
    this.wall.spawnWall();
    if (!(await wait())) return;

    report(76, 'บรรจุปืน');
    this.gun = new GunViewmodel(this.camera);
    if (!(await wait())) return;

    report(84, 'จัดแสง');
    this.breakthrough = new Breakthrough(this.scene, {
      glow: this.wallGlow,
      glowSoft: this.wallGlowSoft,
      backLight: this.backLight,
    });

    this.fxGroup = new THREE.Group();
    this.scene.add(this.fxGroup);

    this.rageBullets = new RageBulletDom();
    this.rageInput = new RageInputDom({
      onSubmit: (text) => this.queuePhrase(text),
      onClose: () => {
        if (this.isMobile) {
          this.pushHud();
          return;
        }
        if (!this.pointerLocked) {
          this.renderer.domElement.requestPointerLock?.();
        }
      },
    });
    if (!(await wait())) return;

    report(88, 'กำลังโหลดเพลง');
    await this.bgm?.preload?.();
    if (this._aborted) return;
    if (!(await wait())) return;

    report(94, 'เกือบพร้อม');
    this.bindInput();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);

    this.clock.start();
    this.lastRemaining = this.wall.getRemaining();
    this.pushHud();
    this.renderer.render(this.scene, this.camera);
    if (!(await wait())) return;

    report(100, 'พร้อมแล้ว');
    await new Promise((r) => window.setTimeout(r, 220));
    if (this._aborted) return;

    this.running = true;
    this.onReady?.();
    this.loop();
  }

  buildEnvironment() {
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(50, 50),
      new THREE.MeshStandardMaterial({ color: 0x120c0a, roughness: 1, metalness: 0 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -4.9;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const glowGeo = new THREE.PlaneGeometry(10, 9);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xffd090,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
      fog: false,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    glow.position.set(0, 0.2, T3.WALL_Z - 5.5);
    this.scene.add(glow);
    this.wallGlow = glow;

    const glowSoft = new THREE.Mesh(
      new THREE.PlaneGeometry(14, 12),
      new THREE.MeshBasicMaterial({
        color: 0xffaa55,
        transparent: true,
        opacity: 0.18,
        side: THREE.DoubleSide,
        fog: false,
      })
    );
    glowSoft.position.set(0, 0.2, T3.WALL_Z - 5.2);
    this.scene.add(glowSoft);
    this.wallGlowSoft = glowSoft;

    this.backLight = new THREE.PointLight(COLORS.LIGHT_WARM, 90, 32, 1.4);
    this.backLight.position.set(0, 0.4, T3.WALL_Z - 5);
    this.scene.add(this.backLight);

    const ember = new THREE.PointLight(0xff8844, 28, 16, 2);
    ember.position.set(1.2, -1.2, T3.WALL_Z - 3.2);
    this.scene.add(ember);

    const key = new THREE.DirectionalLight(0xffe0c8, 0.25);
    key.position.set(1.5, 5, 3);
    this.scene.add(key);

    this.scene.add(new THREE.AmbientLight(0x1a1010, 0.22));
    this.scene.add(new THREE.HemisphereLight(0x2a1814, 0x050303, 0.28));

    const roomMat = new THREE.MeshStandardMaterial({ color: 0x100c0a, roughness: 1 });
    const left = new THREE.Mesh(new THREE.BoxGeometry(1.2, 14, 24), roomMat);
    left.position.set(-8.2, 0, -5);
    this.scene.add(left);
    const right = left.clone();
    right.position.x = 8.2;
    this.scene.add(right);

    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(24, 28), roomMat);
    ceil.rotation.x = Math.PI / 2;
    ceil.position.set(0, 5.5, -5);
    this.scene.add(ceil);
  }

  bindInput() {
    const el = this.renderer.domElement;
    el.style.touchAction = 'none';

    el.addEventListener('click', () => {
      this.ensureAudio();
      if (this.rageInput?.open || this.stageClear) return;
      if (this.isMobile) {
        this.startMobile();
        return;
      }
      if (!this.pointerLocked) el.requestPointerLock?.();
    });
    document.addEventListener('pointerlockchange', this._onLockChange);
    document.addEventListener('mousemove', this._onMove);
    el.addEventListener('mousedown', this._onDown);
    window.addEventListener('mouseup', this._onUp);
    window.addEventListener('keydown', this._onKey);

    el.addEventListener('touchstart', this._onTouchStart, { passive: false });
    el.addEventListener('touchmove', this._onTouchMove, { passive: false });
    el.addEventListener('touchend', this._onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', this._onTouchEnd, { passive: false });
  }

  startMobile() {
    if (!this.isMobile) return;
    this.ensureAudio();
    if (!this.mobileStarted) {
      this.mobileStarted = true;
      this.markPlayStart();
      this.pushHud();
    }
  }

  setFiring(on) {
    if (!this.isPlaying() && on) return;
    this.ensureAudio();
    this.isFiring = !!on;
  }

  /** ปุ่มทดสอบ — พังกำแพงทั้งแผ่นแล้วไปหน้าสำรวจอารมณ์ */
  debugCollapseWall() {
    if (this.stageClear || !this.wall) return;
    this.wall.clear();
    this.wall.remaining = 0;
    this.lastRemaining = 0;
    this.sfx?.playFinale?.();
    this.onStageCleared();
  }

  /** มือถือ — กดปุ่มยิง + ลากบนปุ่มเพื่อเลื่อนเป้า */
  beginAimFire(clientX, clientY) {
    if (!this.isMobile) return;
    this.startMobile();
    if (this.rageInput?.open || this.stageClear || this.paused) return;
    this.ensureAudio();
    this.isFiring = true;
    this.fireLookActive = true;
    this.fireLookLastX = clientX;
    this.fireLookLastY = clientY;
    this.sfx.armHaptics();
    this.sfx.flushHaptics();
    if (!this._onHapticFlush) {
      this._onHapticFlush = () => this.sfx.flushHaptics();
      window.addEventListener('pointermove', this._onHapticFlush, { passive: true });
      window.addEventListener('touchmove', this._onHapticFlush, { passive: true });
    }
  }

  moveAimFire(clientX, clientY) {
    if (!this.isMobile || !this.fireLookActive) return;
    if (!this.mobileStarted || this.paused || this.rageInput?.open) return;
    this.sfx.flushHaptics();
    const sens = T3.TOUCH_LOOK_SENS ?? 0.0048;
    const dx = clientX - this.fireLookLastX;
    const dy = clientY - this.fireLookLastY;
    this.fireLookLastX = clientX;
    this.fireLookLastY = clientY;
    this.yaw -= dx * sens;
    this.pitch -= dy * sens;
    this.pitch = Math.max(T3.PITCH_MIN, Math.min(T3.PITCH_MAX, this.pitch));
  }

  endAimFire() {
    if (!this.isMobile) return;
    this.fireLookActive = false;
    this.isFiring = false;
    if (this._onHapticFlush) {
      window.removeEventListener('pointermove', this._onHapticFlush);
      window.removeEventListener('touchmove', this._onHapticFlush);
      this._onHapticFlush = null;
    }
  }

  nextWave() {
    if (!this.stageClear && this.wall.getRemaining() > 0) return;
    this.endBreakthrough();
    this.stageClear = false;
    this.wall.wave += 1;
    this.wall.spawnWall();
    this.lastRemaining = this.wall.getRemaining();
    this.combo = 0;
    this.waveMaxCombo = 0;
    this.berserkUsedThisWave = false;
    this.waveStartedAt = Date.now();
    this.isFiring = false;
    this.bgm?.setCalm?.(false);
    this.bgm?.setIntensity?.(1);
    this.refillAmmo();
    this.pushHud();
    // เดสก์ท็อป — ล็อกเมาส์ใหม่เพื่อเล่นต่อ
    if (!this.isMobile) {
      this.renderer?.domElement?.requestPointerLock?.();
    }
  }

  returnToMain() {
    this.endPlaySession('return');
    const url = this.getReturnUrl();
    if (url) {
      window.location.href = url;
      return;
    }
    this.restartGame();
  }

  /** กลับหน้าเริ่มเกม + ล้างค่าทั้งหมด */
  restartGame() {
    this.endPlaySession('restart');
    this.stageClear = false;
    this.clearWave = 1;
    this.score = 0;
    this.fireCooldown = 0;
    this.isFiring = false;
    this.yaw = 0;
    this.pitch = 0;
    this.rage = 0;
    this.berserk = false;
    this.berserkTimer = 0;
    this.combo = 0;
    this.comboTimer = 0;
    this.maxCombo = 0;
    this.waveMaxCombo = 0;
    this.berserkUsedThisWave = false;
    this.waveStartedAt = 0;
    this.sessionStartedAt = 0;
    this.shake = 0;
    this.hitStop = 0;
    this.releaseMsg = '';
    this.releaseMsgTimer = 0;
    this.customPhrases = [];
    this.bulletFullAlert = false;
    this.selectedBullets = [];
    this.bulletChoices = (BULLET_CHOICES || []).slice(0, BULLET_BASE_COUNT);
    while (this.bulletChoices.length < BULLET_BASE_COUNT) {
      this.bulletChoices.push(RAGE_PHRASES[this.bulletChoices.length] || 'อัด!');
    }
    this.lookTouchId = null;
    this.fireLookActive = false;
    this.aimedBrick = null;
    this.paused = false;
    this.endBreakthrough();

    if (this.wall) {
      this.wall.wave = 1;
      this.wall.setAimHighlight?.(null);
      this.wall.spawnWall();
    }
    this.lastRemaining = this.wall?.getRemaining?.() ?? 0;
    this.refillAmmo();

    this.clearFx();
    this.rageBullets?.clearShots?.();
    this.rageInput?.hide?.();

    if (this.camera) {
      this.camBase.copy(this.camHome);
      this.camera.position.copy(this.camBase);
      this.camera.rotation.set(0, 0, 0);
      this.applyLook();
    }

    // กลับหน้าเริ่ม — ยังไม่เข้าเล่น
    this.mobileStarted = false;
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.pointerLocked = false;

    this.bgm?.setCalm?.(false);
    this.bgm?.setIntensity?.(1);
    this.sfx?.setFiringBass?.(false, 1);
    if (this.backLight) this.backLight.intensity = 85;
    if (this.renderer) this.renderer.toneMappingExposure = 1.05;

    this.pushHud();
  }

  clearFx() {
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      this.fxGroup?.remove(f.mesh);
      f.mesh?.geometry?.dispose?.();
      f.mesh?.material?.map?.dispose?.();
      f.mesh?.material?.dispose?.();
    }
    this.fx.length = 0;
  }

  onStageCleared() {
    if (this.stageClear) return;
    this.stageClear = true;
    this.clearWave = this.wall?.wave ?? 1;
    this.isFiring = false;
    this.fireLookActive = false;
    this.combo = 0;
    this.comboTimer = 0;
    this.releaseMsg = '';
    this.releaseMsgTimer = 0;
    this.surveyReady = false;
    this.surveyTimer = 0.9;
    this.revealT = 0;
    this.revealYaw0 = this.yaw;
    this.revealPitch0 = this.pitch;
    this.camRevealFrom.copy(this.camBase);
    if (this.gun?.group) this.gun.group.visible = false;
    this.wall?.setAimHighlight?.(null);
    this.clearFx();
    this.rageBullets?.clearShots?.();
    this.rageInput?.hide?.();
    this.breakthrough?.show();
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.bgm?.setCalm?.(true);
    this.bgm?.setIntensity?.(1);
    this.emitPlay('wave-clear');
    this.pushHud();
  }

  endBreakthrough() {
    this.breakthrough?.hide();
    this.surveyReady = false;
    this.surveyTimer = 0;
    this.revealT = 0;
    this.camBase.copy(this.camHome);
    if (this.gun?.group) this.gun.group.visible = true;
    this.renderer.toneMappingExposure = 1.05;
  }

  onLockChange() {
    this.pointerLocked = document.pointerLockElement === this.renderer.domElement;
    if (this.pointerLocked) {
      this.ensureAudio();
      this.markPlayStart();
    }
    this.pushHud();
  }

  onPointerMove(e) {
    if (this.isMobile) return;
    if (!this.pointerLocked || this.paused || this.rageInput?.open) return;
    this.yaw -= e.movementX * T3.LOOK_SENS;
    this.pitch -= e.movementY * T3.LOOK_SENS;
    this.pitch = Math.max(T3.PITCH_MIN, Math.min(T3.PITCH_MAX, this.pitch));
  }

  onPointerDown() {
    if (this.isMobile) return;
    if (!this.pointerLocked || this.rageInput?.open) return;
    this.ensureAudio();
    this.isFiring = true;
  }

  onPointerUp() {
    if (this.isMobile) return;
    this.isFiring = false;
  }

  onTouchStart(e) {
    if (!this.isMobile) return;
    e.preventDefault();
    this.ensureAudio();
    this.startMobile();
    if (this.rageInput?.open) return;

    for (const t of e.changedTouches) {
      if (this.lookTouchId == null) {
        this.lookTouchId = t.identifier;
        this.lookLastX = t.clientX;
        this.lookLastY = t.clientY;
        break;
      }
    }
  }

  onTouchMove(e) {
    if (!this.isMobile) return;
    e.preventDefault();
    if (!this.mobileStarted || this.paused || this.rageInput?.open) return;

    const sens = T3.TOUCH_LOOK_SENS ?? 0.0048;
    for (const t of e.changedTouches) {
      if (t.identifier !== this.lookTouchId) continue;
      const dx = t.clientX - this.lookLastX;
      const dy = t.clientY - this.lookLastY;
      this.lookLastX = t.clientX;
      this.lookLastY = t.clientY;
      this.yaw -= dx * sens;
      this.pitch -= dy * sens;
      this.pitch = Math.max(T3.PITCH_MIN, Math.min(T3.PITCH_MAX, this.pitch));
    }
  }

  onTouchEnd(e) {
    if (!this.isMobile) return;
    for (const t of e.changedTouches) {
      if (t.identifier === this.lookTouchId) {
        this.lookTouchId = null;
      }
    }
  }

  onKey(e) {
    if (this.rageInput?.open || this.stageClear) return;

    if (e.code === 'Tab') {
      e.preventDefault();
      this.openRageInput();
      return;
    }

    if (e.code === 'KeyR') {
      this.reload();
      return;
    }

    const digit = e.code.match(/^Digit([1-9])$/);
    if (digit) {
      const idx = Number(digit[1]) - 1;
      if (idx < (this.bulletChoices?.length || 0)) {
        this.toggleBulletChoice(idx);
      }
    }
  }

  /** เปิด/ปิดคำเสริม — รวมเข้าพูลกระสุนตอนเลือก */
  toggleBulletChoice(index) {
    const max = Math.max(0, (this.bulletChoices?.length || 1) - 1);
    const i = Math.max(0, Math.min(max, index | 0));
    const at = this.selectedBullets.indexOf(i);
    if (at >= 0) this.selectedBullets.splice(at, 1);
    else this.selectedBullets.push(i);
    this.selectedBullets.sort((a, b) => a - b);
    this.pushHud();
    if (!this.isMobile && this.isPlaying()) {
      window.setTimeout(() => {
        this.renderer?.domElement?.requestPointerLock?.();
      }, 0);
    }
  }

  /** ปัดซ้ายลบคำกระสุน — ลบได้ทุกคำ */
  removeBulletChoice(index) {
    const list = this.bulletChoices || [];
    const i = index | 0;
    if (i < 0 || i >= list.length) return false;
    const word = list[i];
    list.splice(i, 1);
    this.bulletChoices = list;

    if (word && Array.isArray(this.customPhrases)) {
      const at = this.customPhrases.indexOf(word);
      if (at >= 0) this.customPhrases.splice(at, 1);
    }

    this.selectedBullets = (this.selectedBullets || [])
      .filter((sel) => sel !== i)
      .map((sel) => (sel > i ? sel - 1 : sel));

    this.showRelease(`ลบ: ${word}`);
    this.pushHud();
    return true;
  }

  openRageInput() {
    this.isFiring = false;
    this.lookTouchId = null;
    this.fireLookActive = false;
    if (document.pointerLockElement) document.exitPointerLock?.();
    if ((this.bulletChoices?.length || 0) >= BULLET_MAX_COUNT) {
      this.rageInput?.hide?.();
      this.bulletFullAlert = true;
      this.pushHud();
      return;
    }
    this.rageInput?.show();
    this.rageInput?.focusInput?.();
    this.pushHud();
  }

  dismissBulletFullAlert() {
    this.bulletFullAlert = false;
    this.pushHud();
  }

  toggleMute() {
    this.sfx.toggle();
    this.pushHud();
    return !this.sfx.enabled;
  }

  toggleMusic() {
    this.bgm.toggle();
    if (this.bgm.enabled) this.bgm.start();
    this.pushHud();
    return !this.bgm.enabled;
  }

  setMusicVolume(v) {
    this.ensureAudio();
    this.bgm.setUserVolume(v);
    this.pushHud();
  }

  setSfxVolume(v) {
    this.ensureAudio();
    this.sfx.setUserVolume(v);
    this.pushHud();
  }

  ensureAudio() {
    this.sfx.unlock();
    this.bgm.unlock();
    this.bgm.start();
  }

  queuePhrase(text) {
    const t = String(text || '').trim().slice(0, 24);
    if (!t) return;

    let idx = this.bulletChoices.indexOf(t);
    if (idx < 0) {
      if (this.bulletChoices.length >= BULLET_MAX_COUNT) {
        this.rageInput?.hide?.();
        this.bulletFullAlert = true;
        this.pushHud();
        return;
      }
      this.bulletChoices.push(t);
      idx = this.bulletChoices.length - 1;
    }

    if (!this.customPhrases.includes(t)) {
      this.customPhrases.push(t);
    }
    if (this.customPhrases.length > 40) this.customPhrases.shift();

    if (!this.selectedBullets.includes(idx)) {
      this.selectedBullets.push(idx);
      this.selectedBullets.sort((a, b) => a - b);
    }

    this.showRelease(`เพิ่ม: ${t}`);
    this.pushHud();
  }

  nextPhrase() {
    const selected = (this.selectedBullets || [])
      .map((i) => this.bulletChoices[i])
      .filter(Boolean);

    // มีคำที่เลือก (รวมคำที่พิมพ์แล้วใส่ช่อง) → สุ่มจากนั้นบ่อย ๆ แล้วปะกับคำเดิม
    if (selected.length && Math.random() < 0.7) {
      return selected[Math.floor(Math.random() * selected.length)];
    }

    const custom = this.customPhrases || [];
    const pool = custom.length ? RAGE_PHRASES.concat(custom) : RAGE_PHRASES;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  applyLook() {
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
  }

  addShake(amount) {
    this.shake = Math.min(0.12, this.shake + amount);
  }

  addHitStop(sec) {
    this.hitStop = Math.max(this.hitStop, sec);
  }

  addRage(amount) {
    if (this.berserk) return;
    this.rage = Math.min(T3.RAGE_MAX, this.rage + amount);
    if (this.rage >= T3.RAGE_MAX) this.triggerBerserk();
  }

  triggerBerserk() {
    this.berserk = true;
    this.berserkUsedThisWave = true;
    this.berserkTimer = T3.BERSERK_DURATION;
    this.rage = T3.RAGE_MAX;
    this.sfx.playBerserkOn();
    this.bgm.setIntensity(1.45);
    this.showRelease('บ้าคลั่ง!');
    this.addShake(T3.SHAKE_BERSERK * 2);
  }

  bumpCombo() {
    this.combo += 1;
    this.maxCombo = Math.max(this.maxCombo, this.combo);
    this.waveMaxCombo = Math.max(this.waveMaxCombo, this.combo);
    this.comboTimer = T3.COMBO_TIMEOUT;
    let msg = null;
    for (const r of RELEASE_MESSAGES) {
      if (this.combo === r.min) msg = r.text;
    }
    if (msg) this.showRelease(msg);
    this.pushHud();
  }

  showRelease(text) {
    this.releaseMsg = text;
    this.releaseMsgTimer = 1.4;
  }

  tryShoot() {
    // กระสุนหมด (โหมดจำกัด)
    if (!this.ammoUnlimited && this.ammo <= 0) {
      this.fireCooldown = 0.18;
      this.sfx.playEmpty();
      return;
    }

    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hits = this.raycaster.intersectObjects(this.wall.group.children, true);
    let hitPoint = null;
    let targetBrick = null;

    for (const hit of hits) {
      const b = this.wall.brickFromHit(hit.object);
      if (b?.alive && b.exposed) {
        targetBrick = b;
        hitPoint = hit.point.clone();
        break;
      }
    }

    if (!hitPoint || !targetBrick) {
      this.fireCooldown = 0.04;
      return;
    }

    if (!this.ammoUnlimited) {
      this.ammo = Math.max(0, this.ammo - 1);
    }

    const rate = this.berserk ? T3.FIRE_RATE * T3.BERSERK_FIRE_MULT : T3.FIRE_RATE;
    this.fireCooldown = rate;
    this.gun.fire();
    this.sfx.playShoot(this.berserk);
    this.sfx.pulseBreak();
    this.spawnRageBullet(hitPoint, targetBrick);
    this.pushHud();
  }

  /** เติมแม็กจากกระสุนสำรอง — พร้อมใช้ตอนปิด unlimited */
  reload() {
    if (this.ammoUnlimited) return false;
    if (this.ammo >= this.ammoMag || this.ammoReserve <= 0) return false;
    const need = this.ammoMag - this.ammo;
    const take = Math.min(need, this.ammoReserve);
    this.ammo += take;
    this.ammoReserve -= take;
    this.pushHud();
    return true;
  }

  refillAmmo() {
    this.ammo = this.ammoMag;
    this.ammoReserve = T3.AMMO_RESERVE ?? this.ammoReserve;
    this.pushHud();
  }

  spawnRageBullet(endPoint, targetBrick) {
    const hitPoint = endPoint.clone();
    let arrived = false;
    const arrive = () => {
      if (arrived) return;
      arrived = true;
      this.resolveWordHit(hitPoint, targetBrick);
    };

    const phrase = this.nextPhrase();
    const muzzle = this.gun.getMuzzleWorld(this.tmpV2);
    const fromScreen = this.worldToScreen(muzzle);
    const toScreen = this.worldToScreen(endPoint);

    this.rageBullets?.spawn(fromScreen, toScreen, arrive, phrase, {
      berserk: this.berserk,
      big: this.berserk || phrase.length > 6,
    });

    window.setTimeout(arrive, ((T3.WORD_FLIGHT ?? 0.4) + 0.12) * 1000);
    this.pushHud();
  }

  worldToScreen(world) {
    const v = this.tmpV.copy(world).project(this.camera);
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    if (v.z > 1) {
      return { x: w * 0.7, y: h * 0.78 };
    }
    return {
      x: (v.x * 0.5 + 0.5) * w,
      y: (-v.y * 0.5 + 0.5) * h,
    };
  }

  resolveWordHit(hitPoint, targetBrick) {
    if (!hitPoint) return;
    this.spawnImpact(hitPoint);

    if (!targetBrick?.alive || !targetBrick.exposed) return;

    let dmg = randInt(T3.DAMAGE_MIN, T3.DAMAGE_MAX);
    if (this.berserk) dmg += T3.BERSERK_DMG_BONUS;

    const label = targetBrick.targetLabel;
    const result = this.wall.dig(targetBrick.col, targetBrick.row, dmg);

    if (result.dealt > 0) {
      this.score += result.dealt;
      this.spawnDamageNumber(hitPoint, result.dealt);
      if (label) this.sfx.playFaceHit();
      this.addRage(T3.RAGE_PER_HIT);
      this.bumpCombo();
      this.addShake(T3.SHAKE_HIT * (this.berserk ? 1.4 : 1));
      this.addHitStop(T3.HITSTOP_HIT);
    }

    if (result.destroyed.length) {
      this.score += result.destroyed.length * 10;
      if (label) {
        this.score += 25;
        this.spawnTargetDissolve(hitPoint, label);
        this.sfx.playTargetPop();
        this.showRelease(`สลาย: ${label}`);
      }
      this.spawnDebris(hitPoint);
      this.sfx.playBreak(this.combo);
      this.sfx.vibrateBreak(this.combo, result.destroyed.length);
      this.addRage(T3.RAGE_PER_BREAK);
      this.addShake(T3.SHAKE_BREAK * (this.berserk ? 1.5 : 1));
      this.addHitStop(T3.HITSTOP_BREAK);

      const rem = this.wall.getRemaining();
      if (this.lastRemaining > 0 && rem <= 0) {
        this.sfx.playFinale();
        this.sfx.vibrateFinale();
        this.addShake(0.08);
        this.addHitStop(0.12);
        this.onStageCleared();
      }
      this.lastRemaining = rem;
    }

    this.pushHud();
  }

  spawnTargetDissolve(point, label) {
    const spr = createDissolveSprite(label);
    spr.position.copy(point);
    spr.position.y += 0.55;
    spr.position.z += 0.85;
    this.fxGroup.add(spr);
    this.fx.push({
      t: 1.1,
      max: 1.1,
      mesh: spr,
      dissolve: true,
      vy: 2.4,
      vz: 0.6,
    });
  }

  spawnImpact(point) {
    const smoke = this.makeSmokeSprite(0.5);
    smoke.position.copy(point);
    smoke.position.z += 0.15;
    this.fxGroup.add(smoke);
    this.fx.push({
      t: 0.55,
      max: 0.55,
      mesh: smoke,
      smoke: true,
      vel: new THREE.Vector3((Math.random() - 0.5) * 0.3, 0.4, 0.2),
    });
  }

  spawnDebris(point) {
    for (let i = 0; i < 5; i++) {
      const chunk = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.06, 0.05),
        new THREE.MeshStandardMaterial({ color: 0xa84a30, roughness: 1 })
      );
      chunk.position.copy(point);
      this.fxGroup.add(chunk);
      this.fx.push({
        t: 0.7,
        max: 0.7,
        mesh: chunk,
        spark: true,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 3,
          1 + Math.random() * 2,
          (Math.random() - 0.5) * 2
        ),
      });
    }
  }

  makeSmokeSprite(opacity = 0.6) {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
    g.addColorStop(0, `rgba(230,220,210,${opacity})`);
    g.addColorStop(0.45, `rgba(160,145,130,${opacity * 0.4})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(canvas);
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      opacity: 1,
      fog: false,
    }));
    spr.scale.set(0.35, 0.35, 1);
    return spr;
  }

  spawnDamageNumber(hitPoint, amount) {
    const worldH = T3.DMG_WORLD_H ?? 0.75;
    const spr = createPixelNumberSprite(amount, { scale: 10, worldHeight: worldH });
    spr.position.copy(hitPoint);
    spr.position.x += (Math.random() - 0.5) * 0.35;
    spr.position.y += 0.2 + Math.random() * 0.2;
    spr.position.z += 0.35;
    spr.scale.multiplyScalar(0.55);
    this.fxGroup.add(spr);
    this.fx.push({
      t: 0.42,
      max: 0.42,
      mesh: spr,
      float: true,
      pop: true,
      baseSX: spr.scale.x / 0.55,
      baseSY: spr.scale.y / 0.55,
      vy: 2.2 + Math.random() * 0.6,
      vx: (Math.random() - 0.5) * 0.4,
    });
  }

  updateFx(dt) {
    this.rageBullets?.update?.(dt);

    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      f.t -= dt;
      const k = Math.max(0, f.t / f.max);
      const u = 1 - k;

      if (f.spark && f.vel) {
        f.mesh.position.addScaledVector(f.vel, dt);
        f.vel.y -= 6 * dt;
        if (f.mesh.material?.opacity != null) f.mesh.material.opacity = k;
      }
      if (f.smoke && f.vel) {
        f.mesh.position.addScaledVector(f.vel, dt);
        const sc = 0.35 + (1 - k) * 1.1;
        f.mesh.scale.set(sc, sc, 1);
        f.mesh.material.opacity = k * 0.85;
      }
      if (f.float) {
        f.mesh.position.y += f.vy * dt;
        f.mesh.position.x += (f.vx || 0) * dt;
        f.mesh.material.opacity = Math.min(1, k * 1.4);
        if (f.pop && f.baseSX) {
          const pop = u < 0.1 ? 0.55 + (u / 0.1) * 0.55 : 1.05 - (u - 0.1) * 0.15;
          f.mesh.scale.set(f.baseSX * pop, f.baseSY * pop, 1);
        }
      }
      if (f.dissolve) {
        f.mesh.position.y += (f.vy || 1) * dt;
        f.mesh.position.z += (f.vz || 0) * dt;
        f.mesh.material.opacity = k;
        const s = 1 + u * 0.9;
        f.mesh.scale.set(1.8 * s, 0.7 * s, 1);
      }
      if (f.fade && f.mesh.material) {
        f.mesh.material.opacity = k * 0.22;
      }

      if (f.t <= 0) {
        this.fxGroup.remove(f.mesh);
        f.mesh.geometry?.dispose?.();
        f.mesh.material?.map?.dispose?.();
        f.mesh.material?.dispose?.();
        this.fx.splice(i, 1);
      }
    }
  }

  pushHud() {
    this.onHud({
      score: this.score,
      remaining: this.wall?.getRemaining?.() ?? 0,
      remainingTotal: this.wall?.getTotal?.() ?? 0,
      wave: this.wall?.wave ?? 1,
      locked: this.isMobile ? this.mobileStarted : this.pointerLocked,
      playing: this.isMobile ? this.mobileStarted : this.pointerLocked,
      mobile: this.isMobile,
      rage: this.rage,
      rageMax: T3.RAGE_MAX,
      berserk: this.berserk,
      combo: this.combo,
      comboMax: T3.COMBO_TIMEOUT,
      comboLeft: this.comboTimer,
      releaseMsg: this.releaseMsg,
      queueLen: this.customPhrases.length,
      typing: !!this.rageInput?.open,
      muted: !this.sfx.enabled || this.sfx.getUserVolume() <= 0.001,
      musicMuted: !this.bgm.enabled || this.bgm.getUserVolume() <= 0.001,
      sfxVolume: this.sfx.getUserVolume(),
      musicVolume: this.bgm.getUserVolume(),
      stageClear: this.stageClear,
      surveyReady: !!this.surveyReady,
      clearWave: this.clearWave,
      returnLabel: T3.RETURN_LABEL || 'กลับไปเล่นเกมเดิมพัน',
      ammo: this.ammo,
      ammoReserve: this.ammoReserve,
      ammoMag: this.ammoMag,
      ammoUnlimited: this.ammoUnlimited,
      bulletChoices: this.bulletChoices.slice(),
      selectedBullets: this.selectedBullets.slice(),
      bulletFullAlert: !!this.bulletFullAlert,
      userId: this.urlParams?.userId || '',
      brand: this.urlParams?.brand || '',
      source: this.urlParams?.source || '',
    });
  }

  loop = () => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.loop);

    const rawDt = Math.min(0.05, this.clock.getDelta());
    if (this.paused) {
      this.renderer.render(this.scene, this.camera);
      return;
    }

    // Hit stop — หยุดเฟรมสั้นๆ
    if (this.hitStop > 0) {
      this.hitStop -= rawDt;
      this.applyLook();
      this.applyCameraShake(0);
      this.renderer.render(this.scene, this.camera);
      return;
    }

    const dt = rawDt;

    // Combo timeout
    if (this.combo > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) {
        this.combo = 0;
        this.comboTimer = 0;
        this.pushHud();
      } else {
        this._comboHudAcc = (this._comboHudAcc || 0) + dt;
        if (this._comboHudAcc >= 0.05) {
          this._comboHudAcc = 0;
          this.pushHud();
        }
      }
    }

    // Release message
    if (this.releaseMsgTimer > 0) {
      this.releaseMsgTimer -= dt;
      if (this.releaseMsgTimer <= 0) {
        this.releaseMsg = '';
        this.pushHud();
      }
    }

    // Berserk
    if (this.berserk) {
      this.berserkTimer -= dt;
      this.rage = (this.berserkTimer / T3.BERSERK_DURATION) * T3.RAGE_MAX;
      this.addShake(T3.SHAKE_BERSERK * dt * 3);
      if (this.berserkTimer <= 0) {
        this.berserk = false;
        this.rage = 0;
        this.bgm.setIntensity(1);
        this.showRelease('หายใจ...');
        this.pushHud();
      }
    } else if (!this.isFiring && this.rage > 0) {
      this.rage = Math.max(0, this.rage - T3.RAGE_IDLE_DRAIN * dt);
    }

    // Bass while firing
    const bassOn = this.isFiring && this.isPlaying();
    this.sfx.setFiringBass(bassOn, this.berserk ? 1.6 : 1 + Math.min(1, this.combo * 0.05));

    if (this.stageClear) this.updateBreakthrough(dt);
    this.applyLook();
    this.applyCameraShake(dt);
    this.gun.update(dt);

    if (!this.stageClear && this.backLight) {
      const pulse = this.berserk ? 30 : 12;
      this.backLight.intensity = 85 + Math.sin(this.clock.elapsedTime * (this.berserk ? 6 : 2.2)) * pulse;
    }
    this.renderer.toneMappingExposure = this.stageClear
      ? 1.12 + Math.min(0.18, this.revealT * 0.18)
      : (this.berserk ? 1.18 : 1.05);

    this.fireCooldown = Math.max(0, this.fireCooldown - dt);
    if (this.isFiring && this.isPlaying() && this.fireCooldown <= 0) {
      this.tryShoot();
    }

    this.updateAimHighlight();
    this.updateFx(dt);
    this.renderer.render(this.scene, this.camera);

    // HUD เบาๆ ทุกเฟรมตอน berserk / rage เปลี่ยน
    if (this.berserk || this.shake > 0.01) this.pushHud();
  };

  updateBreakthrough(dt) {
    this.breakthrough?.update(dt, this.clock.elapsedTime);
    this.revealT = Math.min(1, this.revealT + dt / 1.35);
    const k = 1 - (1 - this.revealT) ** 3;
    this.yaw = this.revealYaw0 * (1 - k);
    this.pitch = this.revealPitch0 * (1 - k);
    this.camBase.lerpVectors(this.camRevealFrom, this.camRevealTo, k);
    if (!this.surveyReady) {
      this.surveyTimer -= dt;
      if (this.surveyTimer <= 0) {
        this.surveyReady = true;
        this.pushHud();
      }
    }
  }

  applyCameraShake(dt) {
    if (dt > 0) this.shake *= Math.max(0, 1 - dt * 7);
    const s = this.shake;
    if (s > 0.001) {
      this.shakeV.set(
        (Math.random() - 0.5) * s,
        (Math.random() - 0.5) * s * 0.7,
        (Math.random() - 0.5) * s * 0.35
      );
    } else {
      this.shakeV.set(0, 0, 0);
    }
    this.camera.position.copy(this.camBase).add(this.shakeV);
  }

  updateAimHighlight() {
    if (!this.wall || !this.isPlaying()) {
      this.wall?.setAimHighlight(null);
      this.aimedBrick = null;
      return;
    }

    this.raycaster.setFromCamera(this.ndc, this.camera);
    const hits = this.raycaster.intersectObjects(this.wall.group.children, true);
    let target = null;
    for (const hit of hits) {
      const b = this.wall.brickFromHit(hit.object);
      if (b?.alive && b.exposed) {
        target = b;
        break;
      }
    }

    if (target !== this.aimedBrick) {
      this.aimedBrick = target;
      this.wall.setAimHighlight(target);
    } else if (target && !target.alive) {
      this.aimedBrick = null;
      this.wall.setAimHighlight(null);
    } else if (target) {
      this.wall.setAimHighlight(target);
    } else {
      this.wall.setAimHighlight(null);
    }
  }

  handleResize() {
    const w = Math.max(1, Math.round(this.container.clientWidth || window.innerWidth));
    const h = Math.max(1, Math.round(this.container.clientHeight || window.innerHeight));
    if (w === this._lastResizeW && h === this._lastResizeH) return;
    this._lastResizeW = w;
    this._lastResizeH = h;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    // false = don't overwrite CSS size; layout stays stable on mobile chrome toggles
    this.renderer.setSize(w, h, false);
  }

  destroy() {
    this.endPlaySession('abandoned');
    this._aborted = true;
    this.running = false;
    cancelAnimationFrame(this.raf);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    this.sfx.setFiringBass(false);
    if (this._onHapticFlush) {
      window.removeEventListener('pointermove', this._onHapticFlush);
      window.removeEventListener('touchmove', this._onHapticFlush);
      this._onHapticFlush = null;
    }
    this.bgm?.dispose();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onResize);
    document.removeEventListener('pointerlockchange', this._onLockChange);
    document.removeEventListener('mousemove', this._onMove);
    window.removeEventListener('mouseup', this._onUp);
    window.removeEventListener('keydown', this._onKey);
    const el = this.renderer?.domElement;
    if (el) {
      el.removeEventListener('touchstart', this._onTouchStart);
      el.removeEventListener('touchmove', this._onTouchMove);
      el.removeEventListener('touchend', this._onTouchEnd);
      el.removeEventListener('touchcancel', this._onTouchEnd);
    }
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.wall?.dispose();
    this.breakthrough?.dispose();
    this.gun?.dispose();
    this.rageBullets?.clear();
    this.rageInput?.dispose();
    this.renderer?.dispose();
    this.renderer?.domElement?.parentNode?.removeChild(this.renderer.domElement);
  }
}
